import { cssMap as compiledCssMap } from '@compiled/react';
import type { ReactNode, RefObject } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { EmojiProvider } from '@atlaskit/emoji/types';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/utils/combine';
import {
	dropTargetForElements,
	monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import type { TabOrientation, TabParameters } from '../nativeTabsTypes.ts';
import type { TabColorKey } from '../tab-color/tabColorDefinitions.ts';
import { NATIVE_TAB_TYPE, computeClosestPillEdge } from '../utils/dragDropUtils.ts';
import { getTabAxis } from '../utils/tabOrientation.ts';

import { TabBar } from './TabBar.tsx';

const styles = cssMap({
	shell: {
		width: '100%',
	},
	layout: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
		width: '100%',
	},
	verticalLayout: {
		flexDirection: 'row',
		alignItems: 'stretch',
	},
	verticalPanel: {
		flex: '1 1 auto',
		minWidth: 0,
		paddingBlockStart: token('space.100'),
	},
});

// The editor-owned descendants have no styling props. Compiled supports the scoped
// descendant selectors that the restricted @atlaskit/css types cannot express.
const editorStyles = compiledCssMap({
	verticalEditorPanel: {
		display: 'flex',
		flexDirection: 'column',
		// The editor owns these elements and exposes no styling props for them. Direct-child
		// selectors keep this layout scoped to this macro, including when tabs are nested.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Stretch the opaque editor-owned frames container.
		'& > [data-multibodiedextension-frames]': {
			display: 'flex',
			flexDirection: 'column',
			flexGrow: 1,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Stretch the ProseMirror-owned content wrapper without changing its DOM.
		'& > [data-multibodiedextension-frames] > .multiBodiedExtension-content-dom-wrapper': {
			display: 'flex',
			flexDirection: 'column',
			flexGrow: 1,
		},
		// Preserve the editor's display:none on inactive frames; only the visible frame grows.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- The editor owns the frame and its visibility; only its flex sizing is changed.
		'& > [data-multibodiedextension-frames] > .multiBodiedExtension-content-dom-wrapper > [data-extension-frame="true"]':
			{
				flexGrow: 1,
			},
	},
});

export type NativeTabsProps = {
	/** Ordered list of tabs. */
	tabs: ReadonlyArray<TabParameters>;
	/** 0-based index of the currently active tab. */
	activeIndex: number;
	/** Tab selection handler. Called with the new index. */
	onTabClick: (index: number) => void;
	/** "Add tab" handler. Required when `canEdit` is true. */
	onAddTab?: () => void;
	/** Whether editing controls are shown. */
	canEdit: boolean;
	/** Navigation orientation. Existing documents default to horizontal. */
	orientation?: TabOrientation;
	/** Called when an active tab title is renamed. Only relevant when `canEdit` is true. */
	onRenameTab?: (index: number, title: string) => void;
	/** Called when the user clicks "Add emoji" or the emoji on an active tab. Only relevant when `canEdit` is true. */
	onEmojiPickerOpen?: () => void;
	/** Called when the user clicks "Remove emoji". Only relevant when `canEdit` is true. */
	onRemoveEmoji?: () => void;
	/** Whether the emoji picker popup is open. Only relevant when `canEdit` is true. */
	isEmojiPickerOpen?: boolean;
	/** Called when the emoji picker popup should close. Only relevant when `canEdit` is true. */
	onEmojiPickerClose?: () => void;
	/** The React node representing the emoji picker content. Only relevant when `canEdit` is true. */
	emojiPickerContent?: ReactNode;
	/** Called when a tab is reordered via drag-and-drop. Only relevant when `canEdit` is true. */
	onReorderTabs?: (sourceIndex: number, targetIndex: number, edge: Edge) => void;
	/** Called when the user clicks "Move tab left" or triggers ⌘⌃⌥←. Only relevant when `canEdit` is true. */
	onMoveTabPrevious?: () => void;
	/** Called when the user clicks "Move tab right" or triggers ⌘⌃⌥→. Only relevant when `canEdit` is true. */
	onMoveTabNext?: () => void;
	/** Called when the user clicks "Copy link to tab". Only relevant when `canEdit` is true. */
	onCopyLink?: () => void;
	/** Called when the user deletes the active tab from the context menu. Only relevant when `canEdit` is true. */
	onDeleteTab?: (index: number) => void;
	/** Emoji provider for resolving emoji images in tab pills. */
	emojiProvider?: Promise<EmojiProvider>;
	/** Called when the user selects a color for the active tab. */
	onColorChange?: (color: TabColorKey | undefined) => void;
	applyToAll?: boolean;
	onApplyToAllChange?: (value: boolean) => void;
	rootRef?: RefObject<HTMLDivElement>;
	/**
	 * The React node representing the currently active frame body.
	 * `NativeTabs` treats it as opaque content.
	 */
	activeFrame: ReactNode;
};

/**
 * `NativeTabs` — top-level presentational shell of the Native Tabs editor extension.
 *
 * Composes `TabBar` (above) with the caller-supplied active frame body (below)
 * inside a single rounded surface card, and closes the WAI-ARIA `tablist` ↔
 * `tabpanel` link by wrapping the body in a `role="tabpanel"` element with
 * `aria-labelledby` pointing at the active pill.
 */
export const NativeTabs: {
	({
		tabs,
		activeIndex,
		onTabClick,
		onAddTab,
		onRenameTab,
		canEdit,
		orientation,
		onReorderTabs,
		onMoveTabPrevious,
		onMoveTabNext,
		onCopyLink,
		onDeleteTab,
		onEmojiPickerOpen,
		onRemoveEmoji,
		isEmojiPickerOpen,
		onEmojiPickerClose,
		emojiPickerContent,
		emojiProvider,
		onColorChange,
		applyToAll,
		onApplyToAllChange,
		rootRef,
		activeFrame,
	}: NativeTabsProps): React.JSX.Element;
	displayName: string;
} = ({
	tabs,
	activeIndex,
	onTabClick,
	onAddTab,
	onRenameTab,
	canEdit,
	orientation = 'horizontal',
	onReorderTabs,
	onMoveTabPrevious,
	onMoveTabNext,
	onCopyLink,
	onDeleteTab,
	onEmojiPickerOpen,
	onRemoveEmoji,
	isEmojiPickerOpen = false,
	onEmojiPickerClose,
	emojiPickerContent = null,
	emojiProvider,
	onColorChange,
	applyToAll = false,
	onApplyToAllChange,
	rootRef,
	activeFrame,
}: NativeTabsProps): React.JSX.Element => {
	const axis = getTabAxis(orientation);
	const activeTab = tabs[activeIndex];
	const internalRef = useRef<HTMLDivElement>(null);
	const shellRef = rootRef ?? internalRef;
	// Stable identity object shared with TabBar to scope drags to this instance.
	const instanceIdRef = useRef({});
	const [contentAreaDrop, setContentAreaDrop] = useState<{
		index: number;
		edge: Edge;
	} | null>(null);

	// Registry of pill wrapper elements, populated by TabBar via registerPillWrapper.
	const pillWrappersRef = useRef<Array<HTMLElement | null>>([]);

	const registerPillWrapper = useCallback((index: number, element: HTMLElement | null) => {
		pillWrappersRef.current[index] = element;
	}, []);

	useEffect(() => {
		const el = shellRef.current;
		if (!el || !canEdit || !onReorderTabs) {
			return;
		}

		// Track the latest computed drop position so the onDrop callback can use it.
		const latestDrop = { current: null as { index: number; edge: Edge } | null };

		const instanceId = instanceIdRef.current;

		return combine(
			// Catch-all drop target on the shell so the expanded content-area
			// drop detection works — provides a valid drop zone when the cursor
			// is outside individual pill targets.
			dropTargetForElements({
				element: el,
				canDrop: ({ source }) =>
					source.data.type === NATIVE_TAB_TYPE && source.data.instanceId === instanceId,
				getData: () => ({ type: NATIVE_TAB_TYPE }),
			}),
			monitorForElements({
				canMonitor: ({ source }) =>
					source.data.type === NATIVE_TAB_TYPE && source.data.instanceId === instanceId,
				onDrag: ({ source, location }) => {
					// When the cursor is directly over a pill-level drop target,
					// that target handles the indicator — clear the content-area state.
					const overPillTarget = location.current.dropTargets.find(
						(t) => t.data.type === NATIVE_TAB_TYPE && typeof t.data.index === 'number',
					);
					if (overPillTarget) {
						latestDrop.current = null;
						setContentAreaDrop(null);
						return;
					}

					// Cursor is outside all pill targets — compute the closest pill edge
					// along the navigation axis.
					const result = computeClosestPillEdge(
						location.current.input[axis.pointer],
						pillWrappersRef.current,
						orientation,
					);
					if (result && result.index !== (source.data.index as number)) {
						latestDrop.current = result;
						setContentAreaDrop((prev) =>
							prev?.index === result.index && prev?.edge === result.edge ? prev : result,
						);
					} else {
						latestDrop.current = null;
						setContentAreaDrop(null);
					}
				},
				onDrop: ({ source, location }) => {
					// If the drop landed on a pill-level target, TabBar's own monitor handles it.
					// Only handle drops that land outside pill targets (content area / outside).
					const overPillTarget = location.current.dropTargets.find(
						(t) => t.data.type === NATIVE_TAB_TYPE && typeof t.data.index === 'number',
					);
					if (!overPillTarget) {
						const drop = latestDrop.current;
						if (drop) {
							const sourceIndex = source.data.index as number;
							onReorderTabs(sourceIndex, drop.index, drop.edge);
						}
					}
					latestDrop.current = null;
					setContentAreaDrop(null);
				},
			}),
		);
	}, [axis, canEdit, onReorderTabs, orientation, shellRef]);

	const tabBar = (
		<TabBar
			tabs={tabs}
			activeIndex={activeIndex}
			onTabClick={onTabClick}
			onAddTab={onAddTab}
			onRenameTab={onRenameTab}
			canEdit={canEdit}
			orientation={orientation}
			onReorderTabs={onReorderTabs}
			onMoveTabPrevious={onMoveTabPrevious}
			onMoveTabNext={onMoveTabNext}
			onCopyLink={onCopyLink}
			onDeleteTab={onDeleteTab}
			onEmojiPickerOpen={onEmojiPickerOpen}
			onRemoveEmoji={onRemoveEmoji}
			isEmojiPickerOpen={isEmojiPickerOpen}
			onEmojiPickerClose={onEmojiPickerClose}
			emojiPickerContent={emojiPickerContent}
			emojiProvider={emojiProvider}
			onColorChange={onColorChange}
			applyToAll={applyToAll}
			onApplyToAllChange={onApplyToAllChange}
			contentAreaDrop={contentAreaDrop}
			instanceId={instanceIdRef.current}
			registerPillWrapper={registerPillWrapper}
		/>
	);
	const panel = (
		<Box
			role="tabpanel"
			id={activeTab ? `panel-${activeTab.id}` : undefined}
			aria-labelledby={activeTab?.id}
			testId="native-tabs-panel"
			xcss={cx(
				orientation === 'vertical' && styles.verticalPanel,
				orientation === 'vertical' && canEdit && editorStyles.verticalEditorPanel,
			)}
		>
			{activeFrame}
		</Box>
	);

	return (
		<Box ref={shellRef} xcss={styles.shell} testId="native-tabs">
			<Box xcss={cx(styles.layout, orientation === 'vertical' && styles.verticalLayout)}>
				{tabBar}
				{panel}
			</Box>
		</Box>
	);
};

NativeTabs.displayName = 'NativeTabs';
